import * as React from 'react';
import styled from 'styled-components';
import { ensureDateTime, isDateValid, isDateInRange, getLocaleDateString, IDate, getDateFormat, DateTime } from '@agilepoint/utils-date-time';
import { debounce, isString } from '@agilepoint/utils-common';
import { excludeKeys, KeyStrokeListener } from '../../utils';
import { IDatePickerProps } from './date-picker-types';
import { Callout } from '../callout';
import { Calendar, excludeTimeComponent, isSameDate } from '../calendar';
import { HostableSurfaceContext, IHostableSurfaceContextProps } from '../hostable-surface';
import { withCulture } from '../../localization';
import { MaskedInput } from '../masked-input';
import { getMasksByFormat } from './masks';

interface IDatePickerState {
    hideCallout: boolean;
    value?: string | DateTime;
}

const StyledMaskedInput = styled(MaskedInput)`
    width: 240px;
    height: 40px;
    padding: 0px;
    .ap-masked-input {
        padding: 0px 0px 0px 16px;
    }
    .ap-icon {
        margin-right: 16px;
    }
`;

interface IDatePickerBaseProps extends IDatePickerProps {
    culture?: string;
}

class DatePickerBase extends React.PureComponent<IDatePickerBaseProps, IDatePickerState> {
    static contextType = HostableSurfaceContext;
    declare context: IHostableSurfaceContextProps;
    private inputElementRef: React.RefObject<HTMLInputElement>;
    private keyStrokeListener: KeyStrokeListener;
    private calloutRef = React.createRef<HTMLDivElement>();

    static getDerivedStateFromProps(props: IDatePickerBaseProps) {
        const { value, format, minDate, maxDate, culture, allowManualEntry } = props;
        if (value === undefined) {
            return null;
        }
        const dateFormat = format || getDateFormat(culture);
        const parsedDate = ensureDateTime(excludeTimeComponent(value, dateFormat)!, dateFormat);
        const isValid = isDateValid(parsedDate, dateFormat) && isDateInRange(parsedDate, excludeTimeComponent(minDate, dateFormat), excludeTimeComponent(maxDate, dateFormat), dateFormat);
        return { value: (allowManualEntry ? value : (isValid ? parsedDate : undefined)) };
    }

    constructor(props: IDatePickerBaseProps) {
        super(props);
        const { defaultValue = '', value, elementRef } = this.props;
        const date = value ?? defaultValue;
        const format = this.getDateFormat();
        this.inputElementRef = elementRef ?? React.createRef();
        this.keyStrokeListener = new KeyStrokeListener(this.inputElementRef);
        this.state = {
            hideCallout: true,
            value: isDateValid(date, format) ? ensureDateTime(date, format) : undefined
        };
    }

    componentDidMount() {
        const { minDate, maxDate } = this.props;
        const { value } = this.state;
        this.setInputValue(this.getValidInputValue(value));
        this.context.addSurfaceEventListener('surfaceClick', this._onSurfaceClick);
        this.keyStrokeListener.subscribe(this.showCalendarCallout, 'Enter');
        this.keyStrokeListener.subscribe(this.hideCalendarCallout, 'Escape');

        const format = this.getDateFormat();
        if (value || !minDate || !isSameDate(format, minDate, maxDate)) {
            return;
        }
        const date = ensureDateTime(minDate, format).toJSDate();
        this.onDateChange([date]);
    }

    componentDidUpdate(prevProps: IDatePickerBaseProps, prevState: IDatePickerState) {
        const { format } = this.props;
        const { value } = this.state;
        if (prevProps.format === format && value === prevState.value) {
            return;
        }
        this.setInputValue(this.getValidInputValue(value));
    }

    render() {
        const { hidden, culture, className, allowManualEntry, format, ...rest } = this.props;
        const { value } = this.state;
        const datePickerProps = excludeKeys(rest, 'type', 'maxDate', 'minDate', 'elementRef',
            'retainPickerAfterSelection', 'defaultValue', 'value', 'weekStartDay', 'showTodayButton', 'onChange', 'as', 'ref');
        const inputValue = this.getValidInputValue(value);

        return hidden ? null : (
            <>
                <StyledMaskedInput
                    {...datePickerProps}
                    className={className}
                    iconName="ap-calendar2.outline"
                    iconPosition="right"
                    autoComplete="off"
                    value={inputValue}
                    elementRef={this.inputElementRef}
                    readOnly={!allowManualEntry}
                    masks={getMasksByFormat(this.getDateFormat())}
                    onValueChange={this.onInputValueChange}
                    onClick={this._openCallout}
                    onBlur={this._onBlur}
                    onKeyPress={this._onKeyPress} />
                {this.renderCallout()}
            </>
        );
    }

    componentWillUnmount() {
        this.context.removeSurfaceEventListener('surfaceClick', this._onSurfaceClick);
        this.keyStrokeListener.unsubscribe();
    }

    private _onBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
        const { minDate, maxDate } = this.props;
        const format = this.getDateFormat();
        const { target: { value } } = ev;
        const parsedDate = ensureDateTime(value, format);
        const formattedDate = this.getLocaleDateString(parsedDate);
        const isInRange = isDateInRange(parsedDate, excludeTimeComponent(minDate, format), excludeTimeComponent(maxDate, format), format);

        if (!this.isDateValid(formattedDate) || !isInRange) {
            this.setState({
                value: ''
            }, () => {
                this.setFocus();
            });
            this.onChange([], '');
            return;
        }

        const { value: valueFromState } = this.state;
        if (formattedDate === valueFromState) {
            return;
        }

        this.setInputValue(formattedDate);
        this.onChange([parsedDate.toJSDate()], formattedDate);
    }

    private onInputValueChange = (newValue: string) => {
        const { value } = this.state;
        const formattedDate = this.getLocaleDateString(value);
        if (newValue === formattedDate) {
            return;
        }
        this.setState({
            value: newValue
        });
        const format = this.getDateFormat();
        const dates = this.isDateValid(newValue) ? [ensureDateTime(newValue, format).toJSDate()] : [];
        this.onChange(dates, newValue);
    }

    private _openCallout = () => {
        this.showCalendarCallout();
    }

    private showCalendarCallout = () => {
        const { disabled } = this.props;
        if (disabled) {
            return;
        }
        this.setState({ hideCallout: false });
    }

    private hideCalendarCallout = () => {
        this.setState({ hideCallout: true });
    }

    private _onSurfaceClick = (ev: React.MouseEvent) => {
        const { current } = this.inputElementRef;
        if (current === null || current.contains(ev.target as Node)) {
            return;
        }
        this.hideCalendarCallout();
    }

    private renderCallout() {
        const { disabled = false, allowManualEntry } = this.props;
        const { hideCallout } = this.state;
        return (
            <Callout targetRef={this.inputElementRef} elementRef={this.calloutRef} onDismiss={this.hideCalendarCallout}
                closeOnEscape={true} autofocusOnOpen={!allowManualEntry} offset={1}
                hidden={hideCallout || disabled} preventClickEventPropagation>
                {this.renderCalendar()}
            </Callout>
        );
    }

    private renderCalendar = () => {
        const {
            minDate, maxDate, weekStartDay, showTodayButton, hideDateFromOtherMonth,
            numberOfMonthsInView, showWeekNumber
        } = this.props;
        const format = this.getDateFormat();
        const { value } = this.state;
        return (
            <Calendar onChange={this.onDateChange} value={ensureDateTime(value || '', format)} format={format}
                maxDate={maxDate} minDate={minDate} weekStartDay={weekStartDay} showTodayButton={showTodayButton}
                hideDateFromOtherMonth={hideDateFromOtherMonth} numberOfMonthsInView={numberOfMonthsInView}
                showWeekNumber={showWeekNumber} />
        );
    }

    private onChange = debounce((formattedDate: Date[], newValue: string) => {
        const { onChange } = this.props;
        if (onChange === undefined) {
            return;
        }
        onChange(formattedDate, newValue);
    }, 0);

    private onDateChange = (selectedDateValue: Date[]) => {
        const { retainPickerAfterSelection, value } = this.props;

        if (selectedDateValue.length > 0 && !this.isDateValid(selectedDateValue[0])) {
            return;
        }

        const format = this.getDateFormat();
        const dateString = this.getLocaleDateString(selectedDateValue[0]);
        const { value: valueFromState } = this.state;
        const newDate = ensureDateTime(selectedDateValue[0], format);
        if (newDate === valueFromState) {
            return;
        }
        this.onChange(selectedDateValue, dateString);
        if (value === undefined) {
            this.setState({
                value: newDate
            });
        }

        if (!retainPickerAfterSelection) {
            this.hideCalendarCallout();
        }

        this.setFocus();
    }

    private setInputValue(value: string) {
        const { current } = this.inputElementRef;
        if (current === null) {
            return;
        }
        current.value = value;
    }

    private setFocus() {
        const { current } = this.inputElementRef;
        if (current === null) {
            return;
        }
        current.focus();
    }

    private getLocaleDateString = (date?: IDate) => {
        const { culture } = this.props;
        return date ? getLocaleDateString(date, this.getDateFormat(), culture) : '';
    }

    private isDateValid = (date: IDate) => isDateValid(date, this.getDateFormat())

    private getDateFormat() {
        const { format, culture } = this.props;
        return format || getDateFormat(culture);
    }

    private getValidInputValue(value?: string | DateTime) {
        const updatedvalue = isString(value) ? value : this.getLocaleDateString(value);
        return (updatedvalue === 'Invalid DateTime') ? '' : updatedvalue;
    }

    private _onKeyPress = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        const { hideCallout } = this.state;
        const { current } = this.calloutRef;
        if (ev.key !== 'Tab' || hideCallout || !current) {
            return;
        }
        current.focus();
        ev.preventDefault();
    }
}

export const DatePicker = withCulture(DatePickerBase);
